import { useCallback, useMemo } from 'react'
import { TFunction } from 'i18next'
import { CharacterRole } from 'app/frontend/types/custom'

import { AbilitySpell, AbilityType } from 'app/frontend/types/custom/Abilities'
import RaidPlan, {
  RaidPlanActor,
  RaidPlanCast,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import {
  getAllSpecializations,
  getClass,
} from 'app/frontend/base/services/characterValues'
import {
  getEncounterTimelineData,
  getGeneralTimelineAbilities,
  getRaidPlanAbilities,
} from 'app/frontend/base/services/raidPlanData'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { resolvePhaseTiming } from '../utils/phaseTiming'
import {
  EncounterAssignmentRow,
  resolveEncounterAssignmentRows,
} from '../utils/encounterAssignmentRows'
type EncounterAbilityRow = EncounterAssignmentRow

type PhaseDivider = {
  name: string
  at: number
}

type PhaseBoundaryHandle = {
  id: string
  index: number
  name: string
  at: number
  min: number
  max: number
}

type SpecializationOption = {
  id: number
  name: string
  classId: number
  className: string
  icon: string
  defaultRole: CharacterRole
}

type UseTeamPlanComputedDataOptions = {
  raidPlan?: RaidPlan
  timeline: RaidPlanTimeline
  selectedType: AbilityType
  t: TFunction
}

type TeamPlanComputedData = {
  encounter:
    | {
        id: number
        name: string
        wclId: number
        thumbnailUrl?: string
      }
    | undefined
  maxTime: number
  encounterAbilityRows: EncounterAbilityRow[]
  encounterAssignmentRows: EncounterAbilityRow[]
  phaseDividers: PhaseDivider[]
  phaseBoundaryHandles: PhaseBoundaryHandle[]
  specializations: SpecializationOption[]
  castInfoByActor: Record<
    string,
    {
      duration: number
      cooldown: number
      issue?:
        | { type: 'warning_overlap'; message: string }
        | { type: 'hard_overlap'; message: string }
    }
  >[]
  classColor: (classId?: number) => string
  getActorSpells: (actor: RaidPlanActor) => AbilitySpell[]
  generalAbilities: AbilitySpell[]
  clamp: (value: number, min: number, max: number) => number
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const buildCastRechargeModel = (
  casts: RaidPlanCast[],
  charges: number,
  cooldown: number,
) => {
  const safeCharges = Math.max(1, Math.floor(charges))
  const safeCooldown = Math.max(0, cooldown)
  const sorted = [...casts].sort((a, b) => a.at - b.at)
  const rechargeEnds: number[] = []
  return sorted.reduce<Record<string, { valid: boolean; cooldown: number }>>(
    (acc, cast) => {
      while (rechargeEnds[0] !== undefined && rechargeEnds[0] <= cast.at) {
        rechargeEnds.shift()
      }
      const valid = rechargeEnds.length < safeCharges
      const rechargeStart =
        rechargeEnds.length === 0
          ? cast.at
          : rechargeEnds[rechargeEnds.length - 1]
      const rechargeEnd = rechargeStart + safeCooldown
      rechargeEnds.push(rechargeEnd)
      acc[cast.id] = {
        valid,
        cooldown: Math.floor(Math.max(rechargeEnd - cast.at, 0)),
      }
      return acc
    },
    {},
  )
}

const useTeamPlanComputedData = ({
  raidPlan,
  timeline,
  selectedType,
  t,
}: UseTeamPlanComputedDataOptions) => {
  const {
    data: { currentExpansion },
  } = usePageInfo()

  const abilities = useMemo(() => getRaidPlanAbilities(), [])
  const spellsByClassId = useMemo(() => {
    return Object.entries(abilities.classSpells).reduce<
      Record<string, Record<number, AbilitySpell>>
    >((acc, [classId, spells]) => {
      acc[classId] = spells.reduce<Record<number, AbilitySpell>>(
        (map, spell) => {
          map[spell.id] = spell
          return map
        },
        {},
      )
      return acc
    }, {})
  }, [abilities.classSpells])

  const spellsBySpecId = useMemo(() => {
    return Object.entries(abilities.specSpells).reduce<
      Record<string, Record<number, AbilitySpell>>
    >((acc, [specId, spells]) => {
      acc[specId] = spells.reduce<Record<number, AbilitySpell>>(
        (map, spell) => {
          map[spell.id] = spell
          return map
        },
        {},
      )
      return acc
    }, {})
  }, [abilities.specSpells])

  const getActorSpell = useCallback(
    (actor: RaidPlanActor, spellId: number) => {
      const fromSpec = actor.spec_id
        ? spellsBySpecId[actor.spec_id.toString()]?.[spellId]
        : undefined
      if (fromSpec) return fromSpec
      return spellsByClassId[actor.class_id.toString()]?.[spellId]
    },
    [spellsByClassId, spellsBySpecId],
  )

  const getAllActorSpells = useCallback(
    (actor: RaidPlanActor) => {
      const classSpells = spellsByClassId[actor.class_id.toString()] ?? {}
      const specSpells = actor.spec_id
        ? spellsBySpecId[actor.spec_id.toString()] ?? {}
        : {}
      return { ...classSpells, ...specSpells }
    },
    [spellsByClassId, spellsBySpecId],
  )

  const encounter = useMemo(
    () =>
      currentExpansion.seasons
        .flatMap((season) => season.instances)
        .flatMap((instance) => instance.encounters)
        .find((entry) => entry.id === raidPlan?.encounterId),
    [raidPlan?.encounterId],
  )

  const encounterTimeline = useMemo(() => {
    if (!raidPlan) return undefined
    return getEncounterTimelineData()[raidPlan.encounterId.toString()]
  }, [raidPlan])

  const encounterDifficulty = useMemo(() => {
    if (!encounterTimeline || !raidPlan) return undefined
    return (
      encounterTimeline.difficulties.find(
        (difficulty) => difficulty.difficulty === raidPlan.difficulty,
      ) ?? encounterTimeline.difficulties[0]
    )
  }, [encounterTimeline, raidPlan])

  const generalAbilities = useMemo(() => getGeneralTimelineAbilities(), [])

  const {
    defaultPhaseDurations,
    defaultPhaseStarts,
    effectivePhaseDurations,
    phaseStarts,
    phaseEnds,
  } = useMemo(() => {
    if (!encounterDifficulty) {
      return {
        defaultPhaseDurations: [],
        effectivePhaseDurations: [],
        defaultPhaseStarts: [],
        phaseStarts: [],
        phaseEnds: [],
      }
    }
    return resolvePhaseTiming({
      phases: encounterDifficulty.phases,
      persistedPhaseDurations:
        timeline.phase_durations ?? raidPlan?.timeline?.phase_durations,
    })
  }, [
    encounterDifficulty,
    raidPlan?.timeline?.phase_durations,
    timeline.phase_durations,
  ])

  const maxTime = useMemo(() => {
    const roundUpToThirtySeconds = (value: number) => Math.ceil(value / 30) * 30
    const encounterMax = encounterDifficulty?.maxTime ?? 0
    if (encounterMax > 0) {
      return roundUpToThirtySeconds(Math.max(60, encounterMax))
    }
    const phasesMax = effectivePhaseDurations.reduce(
      (sum, duration) => sum + duration,
      0,
    )
    const actorMax = timeline.actors.reduce((max, actor) => {
      return actor.casts.reduce((innerMax, cast) => {
        if (cast.spell_id === null) return innerMax
        return Math.max(innerMax, cast.at)
      }, max)
    }, 0)
    return roundUpToThirtySeconds(Math.max(60, phasesMax, actorMax))
  }, [encounterDifficulty?.maxTime, effectivePhaseDurations, timeline.actors])

  const encounterAbilityRows = useMemo(() => {
    if (!encounterDifficulty) return []
    return resolveEncounterAssignmentRows({
      encounterDifficulty,
      defaultPhaseStarts,
      effectivePhaseDurations,
      phaseStarts,
      phaseEnds,
      unknownAbilityLabel: t('dashboard.team.plans.unknown-ability'),
    })
  }, [
    defaultPhaseStarts,
    effectivePhaseDurations,
    encounterDifficulty,
    phaseEnds,
    phaseStarts,
    t,
  ])

  const encounterAssignmentRows = useMemo(() => {
    if (!encounterDifficulty) return []
    return resolveEncounterAssignmentRows({
      encounterDifficulty,
      defaultPhaseStarts,
      effectivePhaseDurations,
      phaseStarts,
      phaseEnds,
      unknownAbilityLabel: t('dashboard.team.plans.unknown-ability'),
      customAbilityLabel: t('dashboard.team.plans.assignments.custom'),
      abilityOverridesByKey: timeline.encounter_assignments?.abilities,
    })
  }, [
    defaultPhaseStarts,
    effectivePhaseDurations,
    encounterDifficulty,
    phaseEnds,
    phaseStarts,
    t,
    timeline.encounter_assignments?.abilities,
  ])

  const phaseDividers = useMemo(() => {
    if (!encounterDifficulty) return []
    return encounterDifficulty.phases.map((phase, index) => ({
      name: phase.name,
      at: phaseEnds[index] ?? maxTime,
    }))
  }, [encounterDifficulty, maxTime, phaseEnds])

  const phaseBoundaryHandles = useMemo(() => {
    if (!encounterDifficulty) return []
    return encounterDifficulty.phases.map((phase, index) => {
      const prevBoundary = index === 0 ? 0 : phaseEnds[index - 1] ?? 0
      const currentBoundary = phaseEnds[index] ?? prevBoundary
      const defaultPhaseDuration = defaultPhaseDurations[index] ?? 0
      return {
        id: phase.id,
        index,
        name: phase.name,
        at: currentBoundary,
        min: prevBoundary,
        max: prevBoundary + defaultPhaseDuration,
      }
    })
  }, [defaultPhaseDurations, encounterDifficulty, phaseEnds])

  const specializations = useMemo(() => {
    return Object.entries(getAllSpecializations())
      .flatMap(([id, spec]) => {
        if (typeof spec.class_id !== 'number') return []
        return [
          {
            id: Number(id),
            name: spec.name,
            classId: spec.class_id,
            className:
              getClass(spec.class_id.toString()) || t('shared.unknown'),
            icon: spec.icon,
            defaultRole: spec.default_role as CharacterRole,
          },
        ]
      })
      .sort((a, b) => {
        if (a.className === b.className) return a.name.localeCompare(b.name)
        return a.className.localeCompare(b.className)
      })
  }, [t])

  const classColor = useCallback((classId?: number) => {
    if (classId === undefined || classId === null) return 'var(--color-primary)'
    const className = getClass(classId.toString())
    if (!className) return 'var(--color-primary)'
    return `var(--color-class-${className.toLowerCase().replace(/\s+/g, '-')})`
  }, [])

  const castInfoByActor = useMemo(() => {
    return timeline.actors.map((actor) => {
      const infoByCastId: Record<
        string,
        {
          duration: number
          cooldown: number
          issue?:
            | { type: 'warning_overlap'; message: string }
            | { type: 'hard_overlap'; message: string }
        }
      > = {}
      const castsBySpell = actor.casts.reduce<Record<number, RaidPlanCast[]>>(
        (acc, cast) => {
          if (cast.spell_id === null) return acc
          if (!acc[cast.spell_id]) acc[cast.spell_id] = []
          acc[cast.spell_id].push(cast)
          return acc
        },
        {},
      )

      Object.entries(castsBySpell).forEach(([spellIdRaw, casts]) => {
        const spellId = Number(spellIdRaw)
        const spell = getActorSpell(actor, spellId)
        const duration = spell?.duration ?? 0
        const baseCooldown = Math.max(0, spell?.cooldown ?? 0)
        const baseCharges = Math.max(1, Math.floor(spell?.charges ?? 1))

        const cooldownTalent = (spell?.talents ?? [])
          .flatMap((talent) => {
            const modifiedCooldown = talent.modifies?.cooldown
            if (typeof modifiedCooldown !== 'number') return []
            return [{ name: talent.name, cooldown: modifiedCooldown }]
          })
          .filter((entry) => entry.cooldown < baseCooldown)
          .sort((a, b) => a.cooldown - b.cooldown)[0]

        const chargeTalent = (spell?.talents ?? [])
          .flatMap((talent) => {
            const modifiedCharges = talent.modifies?.charges
            if (typeof modifiedCharges !== 'number') return []
            return [{ name: talent.name, charges: Math.floor(modifiedCharges) }]
          })
          .filter((entry) => entry.charges > baseCharges)
          .sort((a, b) => b.charges - a.charges)[0]

        const talentCharges = chargeTalent?.charges ?? baseCharges
        const talentCooldown = cooldownTalent?.cooldown ?? baseCooldown

        const baseModel = buildCastRechargeModel(
          casts,
          baseCharges,
          baseCooldown,
        )
        const displayModel = buildCastRechargeModel(
          casts,
          talentCharges,
          talentCooldown,
        )
        const chargeModel = buildCastRechargeModel(
          casts,
          talentCharges,
          baseCooldown,
        )
        const cooldownModel = buildCastRechargeModel(
          casts,
          baseCharges,
          talentCooldown,
        )
        const talentModel = buildCastRechargeModel(
          casts,
          talentCharges,
          talentCooldown,
        )

        casts.forEach((cast) => {
          const baseEntry = baseModel[cast.id]
          const displayEntry = displayModel[cast.id]
          const chargeEntry = chargeModel[cast.id]
          const cooldownEntry = cooldownModel[cast.id]
          const talentEntry = talentModel[cast.id]
          if (
            !baseEntry ||
            !displayEntry ||
            !chargeEntry ||
            !cooldownEntry ||
            !talentEntry
          )
            return

          const info: {
            duration: number
            cooldown: number
            issue?:
              | { type: 'warning_overlap'; message: string }
              | { type: 'hard_overlap'; message: string }
          } = {
            duration,
            cooldown: displayEntry.cooldown,
          }

          if (!baseEntry.valid) {
            if (talentEntry.valid) {
              const enabledByCharges = chargeEntry.valid && !baseEntry.valid
              const enabledByCooldown = cooldownEntry.valid && !baseEntry.valid
              const warningTalents = [
                enabledByCharges ? chargeTalent?.name : undefined,
                enabledByCooldown ? cooldownTalent?.name : undefined,
              ].filter((name): name is string => Boolean(name))
              const fallbackTalents = [
                chargeTalent?.name,
                cooldownTalent?.name,
              ].filter((name): name is string => Boolean(name))
              const names = warningTalents.length
                ? warningTalents
                : fallbackTalents
              const talentNamesText =
                names.length > 1
                  ? `${names.slice(0, -1).join(', ')} or ${
                      names[names.length - 1]
                    }`
                  : names[0]
              info.issue = {
                type: 'warning_overlap',
                message: talentNamesText
                  ? `Casts overlap without the ${talentNamesText} talent`
                  : 'Casts overlap without the required talent',
              }
            } else {
              info.issue = {
                type: 'hard_overlap',
                message: 'Casts overlap',
              }
            }
          }

          infoByCastId[cast.id] = info
        })
      })
      return infoByCastId
    })
  }, [getActorSpell, timeline.actors])

  const getActorSpells = useCallback(
    (actor: RaidPlanActor) => {
      return Object.values(getAllActorSpells(actor))
        .filter((spell) => spell.type === selectedType)
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    [getAllActorSpells, selectedType],
  )

  return {
    encounter,
    maxTime,
    encounterAbilityRows,
    encounterAssignmentRows,
    phaseDividers: phaseDividers as PhaseDivider[],
    phaseBoundaryHandles: phaseBoundaryHandles as PhaseBoundaryHandle[],
    specializations: specializations as SpecializationOption[],
    castInfoByActor,
    classColor,
    getActorSpells,
    generalAbilities,
    clamp,
  }
}

export type {
  EncounterAbilityRow,
  PhaseDivider,
  PhaseBoundaryHandle,
  SpecializationOption,
  TeamPlanComputedData,
}
export default useTeamPlanComputedData
